/**
 * Pure helpers — row-height coordination for Time Machine Diff Pair view (#135).
 *
 * Diff Pair shows two grids side-by-side (T1 on the left, T2 on the right) and
 * ties their vertical scroll together so the user can compare T1↔T2 row-by-row.
 * The cell contents on the two sides can differ wildly (e.g., the Unassigned
 * column at end-of-day after most projects have been assigned to crews), and
 * the row's natural height grows with content. If one side's row is taller
 * than the other, the rows below it shift up relative to their counterparts
 * → permanent misalignment in shared-scroll mode.
 *
 * The fix: each visible row has a stable `rowId` ("unassigned" for the
 * Unassigned row, payrollNo for crew rows). Both grids measure their own
 * row heights and report them up to a shared coordinator. The coordinator
 * computes `max(left, right)` for each rowId, and each grid applies that
 * max as `min-height` to its row → both sides render at the taller of the
 * two natural heights → rows align row-by-row regardless of content.
 *
 * Heights only grow during a single Diff Pair session — they don't shrink
 * automatically when content gets smaller, because the applied `min-height`
 * also bumps `offsetHeight` and we can't distinguish "natural content height"
 * from "applied min-height" with a single DOM measurement. To reset:
 * something must force a remount of the row tree (week navigation,
 * exit/re-enter Time Travel, toggle Diff Pair off and on). For the user's
 * pain-point this is acceptable — a slightly oversized row is far less
 * disruptive than the current misalignment.
 *
 * These two functions are pure and adversarially unit-tested. The wrapper
 * component that uses them lives in src/screens/schedule/DiffPairRowSync.js.
 */

/**
 * Returns a new rowHeights object with `rowId.side` set to `height`.
 *
 * Dedup: if the value is already equal to the previous `rowId.side`, returns
 * the SAME object reference. Callers driving React state via this helper rely
 * on reference equality to skip no-op re-renders.
 *
 * @param {Object} rowHeights   shape: { rowId: { left: number, right: number } }
 * @param {string} rowId        stable identifier for the row (e.g. "unassigned", payrollNo)
 * @param {"left"|"right"} side which grid is reporting
 * @param {number} height       measured offsetHeight in CSS pixels (≥ 0)
 * @returns {Object} new rowHeights (or the original reference when no change)
 */
export function recordRowHeight(rowHeights, rowId, side, height) {
  if (rowHeights == null || typeof rowHeights !== "object") return rowHeights;
  if (typeof rowId !== "string" || rowId === "") return rowHeights;
  if (side !== "left" && side !== "right") return rowHeights;
  if (typeof height !== "number" || !Number.isFinite(height) || height < 0) {
    return rowHeights;
  }
  const current = rowHeights[rowId];
  const cur = current || { left: 0, right: 0 };
  if (cur[side] === height) return rowHeights; // no-op
  return {
    ...rowHeights,
    [rowId]: { ...cur, [side]: height },
  };
}

/**
 * Returns the synchronized `min-height` for a rowId — the max of the two sides'
 * reported heights — or `undefined` if no measurements have been recorded yet
 * (in which case the row should fall back to its CSS-defined default).
 *
 * @param {Object} rowHeights  the state map produced by `recordRowHeight`
 * @param {string} rowId
 * @returns {number|undefined}
 */
export function computeSyncedRowMinHeight(rowHeights, rowId) {
  if (rowHeights == null || typeof rowHeights !== "object") return undefined;
  if (typeof rowId !== "string" || rowId === "") return undefined;
  const r = rowHeights[rowId];
  if (!r) return undefined;
  const left = typeof r.left === "number" && r.left > 0 ? r.left : 0;
  const right = typeof r.right === "number" && r.right > 0 ? r.right : 0;
  const max = Math.max(left, right);
  return max > 0 ? max : undefined;
}
